/**
 * Combat System - Two-Dice Redesign
 * 
 * Implements the new two-dice combat system:
 * - Attack die: Determines who wins the round (higher roll wins)
 * - Damage die: Determines damage dealt by round winner
 * - Defense: Reduces incoming damage (minimum 1 damage always dealt)
 * 
 * Player stats: 50 HP, 11 attack, 10 damage, 2 defense
 * Monster stats: Variable HP, attack, damage, and defense values
 * 
 * Note: Party size scaling (2-4 players) is maintained but not actively balanced.
 * The new system is designed and balanced for solo play (party size = 1).
 */

import { Player } from '../types/player';
import { getCreatureStats, CreatureStats } from '../data/bestiary';

export type PartySize = 1 | 2 | 3 | 4;

/**
 * Combat stats for a combatant (player or monster)
 */
export interface CombatStats {
  hp: number;
  attackDie: number;  // Size of die for attack rolls (e.g., 10 for d10)
  damageDie: number;  // Size of die for damage rolls (e.g., 10 for d10)
  defense: number;    // Armor/damage reduction (subtracted from incoming damage, min 1)
}

/**
 * Result of a single combat round
 */
export interface CombatRound {
  // New two-dice system
  playerAttackRoll?: number;
  monsterAttackRoll?: number;
  playerDamageRoll?: number;
  monsterDamageRoll?: number;
  
  // Legacy party system (for backward compatibility)
  partyRolls?: number[];
  partyTotal?: number;
  monsterAttack?: number;
  
  damage: number;
  winner: 'party' | 'monster' | 'player' | 'tie';
}

/**
 * Result of combat round execution
 */
export interface CombatResult {
  round: CombatRound;
  partyHpAfter: number[];
  monsterHpAfter: number;
  isMonsterDefeated: boolean;
  areAllPlayersDefeated: boolean;
}

/**
 * Roll a die of any size
 */
export function rollDie(dieSize: number): number {
  return Math.floor(Math.random() * dieSize) + 1;
}

/**
 * Roll a d10 (1-10) - Legacy function
 */
export function rollD10(): number {
  return rollDie(10);
}

/**
 * Execute a combat round using the NEW two-dice system (solo player)
 * 
 * @param playerStats - Player's combat stats (HP, attackDie, damageDie)
 * @param monsterStats - Monster's combat stats (HP, attackDie, damageDie)
 * @returns Combat round result with dice rolls and damage
 */
export function executeTwoDiceCombatRound(
  playerStats: CombatStats,
  monsterStats: CombatStats
): {
  playerAttackRoll: number;
  monsterAttackRoll: number;
  playerDamageRoll: number;
  monsterDamageRoll: number;
  winner: 'player' | 'monster' | 'tie';
  damageToPlayer: number;
  damageToMonster: number;
} {
  // Roll attack dice for both combatants
  const playerAttackRoll = rollDie(playerStats.attackDie);
  const monsterAttackRoll = rollDie(monsterStats.attackDie);
  
  let playerDamageRoll = 0;
  let monsterDamageRoll = 0;
  let winner: 'player' | 'monster' | 'tie' = 'tie';
  let damageToPlayer = 0;
  let damageToMonster = 0;
  
  // Determine round winner by attack rolls
  if (playerAttackRoll > monsterAttackRoll) {
    // Player wins round - roll damage
    winner = 'player';
    playerDamageRoll = rollDie(playerStats.damageDie);
    // Apply defense: reduce damage but ensure minimum of 1
    damageToMonster = Math.max(1, playerDamageRoll - monsterStats.defense);
  } else if (monsterAttackRoll > playerAttackRoll) {
    // Monster wins round - roll damage
    winner = 'monster';
    monsterDamageRoll = rollDie(monsterStats.damageDie);
    // Apply defense: reduce damage but ensure minimum of 1
    damageToPlayer = Math.max(1, monsterDamageRoll - playerStats.defense);
  }
  // Tie = no damage to either side
  
  return {
    playerAttackRoll,
    monsterAttackRoll,
    playerDamageRoll,
    monsterDamageRoll,
    winner,
    damageToPlayer,
    damageToMonster,
  };
}

/**
 * Simulate a complete combat using the NEW two-dice system
 * 
 * @param playerStats - Player's combat stats
 * @param monsterStats - Monster's combat stats
 * @returns Winner and number of rounds
 */
export function simulateTwoDiceCombat(
  playerStats: CombatStats,
  monsterStats: CombatStats
): { winner: 'player' | 'monster'; rounds: number; playerHPRemaining: number; monsterHPRemaining: number } {
  let playerHP = playerStats.hp;
  let monsterHP = monsterStats.hp;
  let rounds = 0;
  
  while (playerHP > 0 && monsterHP > 0) {
    const result = executeTwoDiceCombatRound(playerStats, monsterStats);
    playerHP -= result.damageToPlayer;
    monsterHP -= result.damageToMonster;
    rounds++;
    
    // Safety limit to prevent infinite loops
    if (rounds > 1000) {
      console.warn('Combat exceeded 1000 rounds, ending');
      return { 
        winner: playerHP > monsterHP ? 'player' : 'monster',
        rounds,
        playerHPRemaining: playerHP,
        monsterHPRemaining: monsterHP
      };
    }
  }
  
  return {
    winner: playerHP > 0 ? 'player' : 'monster',
    rounds,
    playerHPRemaining: Math.max(0, playerHP),
    monsterHPRemaining: Math.max(0, monsterHP)
  };
}

/**
 * Roll d10 for each party member - LEGACY
 */
export function rollPartyAttack(partySize: number): number[] {
  const rolls: number[] = [];
  for (let i = 0; i < partySize; i++) {
    rolls.push(rollD10());
  }
  return rolls;
}

/**
 * Calculate total party attack score - LEGACY
 */
export function calculatePartyTotal(rolls: number[]): number {
  return rolls.reduce((sum, roll) => sum + roll, 0);
}

/**
 * Execute a combat round - LEGACY
 * 
 * @deprecated Use executeTwoDiceCombatRound for new two-dice system
 * @param partyRolls - d10 rolls for each party member
 * @param monsterName - Name of the monster
 * @param partySize - Number of players in the party (1-4)
 * @param currentMonsterHp - Monster's current HP
 * @param partyHp - Current HP for each party member
 * @returns Combat result with damage applied
 */
export function executeCombatRound(
  partyRolls: number[],
  monsterName: string,
  partySize: PartySize,
  currentMonsterHp: number,
  partyHp: number[]
): CombatResult {
  // Get monster stats for this party size
  const monsterStats = getCreatureStats(monsterName, partySize);
  if (!monsterStats) {
    throw new Error(`Monster "${monsterName}" not found in bestiary`);
  }

  // Calculate party total
  const partyTotal = calculatePartyTotal(partyRolls);

  // Compare scores
  const difference = Math.abs(partyTotal - monsterStats.attack);
  let winner: 'party' | 'monster';
  let damage: number;

  if (partyTotal > monsterStats.attack) {
    // Party wins - monster takes damage
    winner = 'party';
    damage = difference;
  } else if (partyTotal < monsterStats.attack) {
    // Monster wins - each player takes damage
    winner = 'monster';
    damage = difference;
  } else {
    // Tie - no damage
    winner = 'party'; // Treat tie as party win (no damage)
    damage = 0;
  }

  // Apply damage
  let monsterHpAfter = currentMonsterHp;
  const partyHpAfter = [...partyHp];

  if (winner === 'party' && damage > 0) {
    // Monster takes damage
    monsterHpAfter = Math.max(0, currentMonsterHp - damage);
  } else if (winner === 'monster' && damage > 0) {
    // Each player takes damage
    for (let i = 0; i < partyHpAfter.length; i++) {
      partyHpAfter[i] = Math.max(0, partyHpAfter[i] - damage);
    }
  }

  // Check win/loss conditions
  const isMonsterDefeated = monsterHpAfter <= 0;
  const areAllPlayersDefeated = partyHpAfter.every(hp => hp <= 0);

  const round: CombatRound = {
    partyRolls,
    partyTotal,
    monsterAttack: monsterStats.attack,
    damage,
    winner,
  };

  return {
    round,
    partyHpAfter,
    monsterHpAfter,
    isMonsterDefeated,
    areAllPlayersDefeated,
  };
}

/**
 * Get monster stats for display
 */
export function getMonsterStatsForDisplay(
  monsterName: string,
  partySize: PartySize
): { hp: number; attack: number } | null {
  return getCreatureStats(monsterName, partySize);
}

/**
 * Initialize monster for combat
 * Returns monster with current HP set to max HP for the party size
 */
export function initializeMonster(
  monsterName: string,
  partySize: PartySize
): { name: string; maxHp: number; currentHp: number; attack: number; attackDie?: number; damageDie?: number; defense?: number } | null {
  const stats = getCreatureStats(monsterName, partySize);
  if (!stats) return null;

  return {
    name: monsterName,
    maxHp: stats.hp,
    currentHp: stats.hp,
    attack: stats.attack,
    attackDie: stats.attackDie,
    damageDie: stats.damageDie,
    defense: stats.defense,
  };
}

